import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

import { fetchAvailabilityEvents } from '../../../../../redux/availability/availabilityThunks';
import { DrawerContentContainer } from '../../../../AdminDrawer/AdminDrawer.styles';
import { selectSelectedEmployee } from '../../../../../redux/people/peopleSlice';
import { PlusIcon } from '../../../../../assets/svg';
import Events from './components/Events/Events';
import {
  setSelectedEvent,
  clearSelectedEvent,
  selectAllEvents,
} from '../../../../../redux/availability/availabilitySlice';
import { AddEventBox, StyledDescriptionText, StyledTitleText } from './UserAvailabilityPanel.styles';
import { selectAvailabilityStatus } from '../../../../../redux/availability/availabilitySlice';
import { LOADING_STATES } from '../../../../../utils/constants/loadingStates';

const UserAvailabilityPanel = ({ handleOpenDrawer, drawerTypes }) => {
  const dispatch = useDispatch();

  const events = useSelector(selectAllEvents);
  const employeeId = useSelector(selectSelectedEmployee)?.id;
  const eventStatus = useSelector(selectAvailabilityStatus);
  const isLoading = eventStatus === LOADING_STATES.LOADING;

  useEffect(() => {
    dispatch(fetchAvailabilityEvents(employeeId));
  }, [dispatch, employeeId]);

  const handleEditEvent = useCallback(
    (event) => {
      dispatch(setSelectedEvent(event));
      handleOpenDrawer(drawerTypes.EDIT_AVAILABILITY, event, 'Edit Availability');
    },
    [dispatch, handleOpenDrawer, drawerTypes]
  );

  const handleAddEvent = useCallback(() => {
    dispatch(clearSelectedEvent());
    handleOpenDrawer(drawerTypes.CREATE_AVAILABILITY, null, 'Add Availability');
  }, [dispatch, handleOpenDrawer, drawerTypes]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ width: '100%', height: '100%' }}
    >
      <DrawerContentContainer alignment="center">
        <StyledDescriptionText variant="bodyRegular">
          Set when you are unavailable to automatically decline shift swap requests at specified times & dates
        </StyledDescriptionText>

        <StyledTitleText variant="navbar">TIMES I'M UNAVAILABLE TO WORK</StyledTitleText>
        <Events events={events} onClick={handleEditEvent} isLoading={isLoading} canEdit={true} />
        <AddEventBox onClick={handleAddEvent}>
          <PlusIcon />
          <Typography variant="bodyRegular">Add Event</Typography>
        </AddEventBox>
      </DrawerContentContainer>
    </motion.div>
  );
};

UserAvailabilityPanel.propTypes = {
  handleOpenDrawer: PropTypes.func.isRequired,
  drawerTypes: PropTypes.shape({
    EDIT_AVAILABILITY: PropTypes.string.isRequired,
    CREATE_AVAILABILITY: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(UserAvailabilityPanel);
